import i18next from 'i18next';
import {CURRENCIES_SIGNS} from '@chekin/core';

const FRACTION_DIGITS = {minimumFractionDigits: 2, maximumFractionDigits: 2} as const;

/**
 * Callers hand us a symbol rather than a currency code — there are 39 of them
 * and none has the code in scope — so recover the code from the symbol.
 */
const CODE_BY_SIGN = Object.entries(CURRENCIES_SIGNS).reduce<Record<string, string>>(
  (codes, [code, sign]) => {
    codes[sign] = code.toUpperCase();
    return codes;
  },
  {},
);

function toNumber(amount: number | string | null | undefined) {
  const parsed = typeof amount === 'string' ? parseFloat(amount) : amount;
  return Number.isFinite(parsed) ? (parsed as number) : 0;
}

/**
 * Amounts read in the guest's language: "12,50 €" in French, "1.234,50 €" in
 * German. Placement and the decimal and grouping marks come from Intl; the
 * symbol stays the one payment settings configured, because Intl would
 * substitute an ISO code for the three currencies it has no localized symbol
 * for (AED, CZK, CHF) and the guest is used to seeing Kč, not CZK.
 *
 * The UI language carries no region, so a browser locale in the same language
 * lends it one: an es-MX browser reading Spanish gets "$1,234.50", es-ES gets
 * "1234,50 €".
 */
function getFormatLocale() {
  const language = i18next.language || 'en';
  const base = language.split('-')[0].toLowerCase();
  const browserLocales =
    typeof navigator === 'undefined' ? [] : navigator.languages ?? [];
  const regional = browserLocales.find(
    locale => locale.split('-')[0].toLowerCase() === base && locale.includes('-'),
  );

  return language.includes('-') ? language : regional ?? language;
}

function formatMoney(amount: number | string | undefined, currencySymbol: string) {
  const value = toNumber(amount);
  const locale = getFormatLocale();
  const currency = CODE_BY_SIGN[currencySymbol];

  try {
    if (currency) {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        ...FRACTION_DIGITS,
      })
        .formatToParts(value)
        .map(part => (part.type === 'currency' ? currencySymbol : part.value))
        .join('');
    }

    return `${currencySymbol}${new Intl.NumberFormat(locale, FRACTION_DIGITS).format(
      value,
    )}`;
  } catch {
    return `${currencySymbol}${value.toFixed(2)}`;
  }
}

function formatMoneyOrEmpty(
  amount: number | string | undefined | null,
  currencySymbol: string,
) {
  if (amount === undefined || amount === null || amount === '') return '';

  return formatMoney(amount, currencySymbol);
}

export {formatMoney, formatMoneyOrEmpty, toNumber};
